'use client'

import styled from '@emotion/styled'
import ClockIcon from '@mui/icons-material/AccessTime'
import BarChartIcon from '@mui/icons-material/BarChart'
import BoltIcon from '@mui/icons-material/Bolt'
import RadioIcon from '@mui/icons-material/CellTower'
import CheckIcon from '@mui/icons-material/CheckCircle'
import DescriptionIcon from '@mui/icons-material/Description'
import GroupIcon from '@mui/icons-material/Group'
import LaunchIcon from '@mui/icons-material/Launch'
import LinkIcon from '@mui/icons-material/Link'
import BookIcon from '@mui/icons-material/MenuBook'
import ShieldIcon from '@mui/icons-material/Shield'
import SpeedIcon from '@mui/icons-material/Speed'
import ActivityIcon from '@mui/icons-material/Timeline'
import TargetIcon from '@mui/icons-material/TrackChanges'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import Box from '@mui/material/Box'
import MuiButton from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { motion } from 'framer-motion'
import { useEffect } from 'react'

const Badge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(39, 177, 198, 0.1);
  border: 1px solid rgba(39, 177, 198, 0.2);
  border-radius: 9999px;
  padding: 6px 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #27b1c6;
  margin-bottom: 24px;
`

const IconBox = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(39, 177, 198, 0.2),
    rgba(39, 177, 198, 0.1)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`

const StyledCard = styled(Card)`
  height: 100%;
  background: #fafafa !important;
  background-image: none !important;
  border: none;
  border-radius: 12px;
  box-shadow:
    0 1px 2px -1px rgba(0, 0, 0, 0.1),
    0 4px 6px -1px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;

  &:hover {
    box-shadow:
      0 1px 2px -1px rgba(0, 0, 0, 0.1),
      0 8px 10px -1px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
  }
`

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0e7490;
  flex-shrink: 0;
  margin-top: 8px;
`

const systemCards = [
  {
    icon: <ActivityIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Trend Networks',
    desc: 'Analyzes Bitcoin dominance, network transaction volumes, and multi-timeframe moving averages to establish primary market direction.',
    bullets: [
      'Daily trend assessment',
      'On-chain analytics integration',
      'Continuous scale from -1 to +1',
    ],
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Direction Networks',
    desc: 'Multi-head CNN architecture with soft attention mechanism processes market data across three timeframes for precise predictions.',
    bullets: [
      'Minute-scale high-frequency signals',
      'Hourly medium-term patterns',
      'Daily long-term context',
      'Orderbook & sentiment analysis',
    ],
    highlight: true,
  },
  {
    icon: <TargetIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Execution Engine',
    desc: 'Real-time ensemble predictions with dynamic confidence scoring and adaptive position sizing for optimal risk management.',
    bullets: [
      'Tick-level trading decisions',
      '100-300ms complete execution',
      'Strict risk controls',
    ],
  },
]

const edgeFeatures = [
  {
    icon: <TargetIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Confidence-Based Execution',
    desc: 'Every trade comes with quantifiable confidence levels, enabling dynamic position sizing based on model certainty rather than fixed allocations.',
  },
  {
    icon: <GroupIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Multi-Model Consensus',
    desc: 'Multiple neural networks must agree before execution, reducing false signals and increasing reliability of trading decisions.',
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Market Regime Adaptation',
    desc: 'Our systems automatically detect market regime changes and adjust strategy parameters to maintain edge across different conditions.',
  },
  {
    icon: <ClockIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Optimal Execution Timing',
    desc: 'Orderbook analysis and microstructure patterns enable precise entry and exit timing to minimize market impact and slippage.',
  },
  {
    icon: <CheckIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Performance Verification',
    desc: "Continuous statistical testing ensures our trading edge remains significant and isn't degrading due to market evolution.",
  },
  {
    icon: <ShieldIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Dynamic Risk Scaling',
    desc: 'Real-time drawdown monitoring with automatic position reduction during adverse periods to preserve capital for optimal opportunities.',
  },
]

const dataSources = [
  {
    icon: <BarChartIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'Market Data',
    desc: 'Traditional OHLCV price and volume information across multiple timeframes from daily to sub-second intervals.',
    metrics: ['Price action', 'Volume patterns', 'Technical indicators'],
  },
  {
    icon: <LinkIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'On-Chain Analytics',
    desc: 'Blockchain network activity, transaction patterns, and holder behavior providing unique insights into adoption trends.',
    metrics: ['Transaction volume', 'Network activity', 'Address analytics'],
  },
  {
    icon: <BookIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'Orderbook Dynamics',
    desc: 'Real-time supply and demand microstructure capturing short-term pressure imbalances that precede price movements.',
    metrics: ['Order size analysis', 'Bid-ask spreads', 'Liquidity depth'],
  },
  {
    icon: <RadioIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'Sentiment Data',
    desc: 'Global news and social media sentiment from GDELT datasets providing 15-minute frequency contextual market mood.',
    metrics: ['News sentiment', 'Social signals', 'Event detection'],
  },
]

const metrics = [
  {
    icon: <TrendingUpIcon />,
    value: '1.15',
    label: 'Profit Factor',
    desc: 'Consistent performance across market conditions',
  },
  {
    icon: <BoltIcon />,
    value: '<50ms',
    label: 'Prediction Latency',
    desc: 'Ultra-fast inference for real-time decisions',
  },
  {
    icon: <SpeedIcon />,
    value: '10K+',
    label: 'Daily Trades',
    desc: 'High-frequency execution across markets',
  },
  {
    icon: <ShieldIcon />,
    value: '24/7',
    label: 'Market Coverage',
    desc: 'Continuous operation and monitoring',
  },
]

function SectionHeader({
  badge,
  title,
  subtitle,
}: {
  badge: string
  title: string
  subtitle: string
}) {
  return (
    <Box sx={{ textAlign: 'center', mb: 8 }}>
      <Badge>{badge}</Badge>
      <Typography variant='h2' sx={{ fontWeight: 700, mb: 2, color: '#171717' }}>
        {title}
      </Typography>
      <Typography
        sx={{ color: '#6b7280', fontSize: '1.125rem', maxWidth: 700, mx: 'auto' }}
      >
        {subtitle}
      </Typography>
    </Box>
  )
}

export default function TechnologyPage() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <Box sx={{ pt: 'var(--header-height)' }}>
      {/* How The System Works */}
      <Box sx={{ py: 12, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth='lg'>
          <SectionHeader
            badge='How It Works'
            title='How The System Works'
            subtitle='A two-stage approach combining trend analysis with high-frequency direction prediction for optimal market timing'
          />

          <Grid container spacing={3}>
            {systemCards.map((card, i) => (
              <Grid key={i} size={{ xs: 12, md: 4 }}>
                <StyledCard
                  elevation={0}
                  sx={
                    card.highlight
                      ? {
                          background:
                            'linear-gradient(135deg, rgba(39,177,198,0.05), transparent)',
                          borderColor: 'rgba(39,177,198,0.3)',
                        }
                      : {}
                  }
                >
                  <CardContent sx={{ p: 4 }}>
                    <IconBox>{card.icon}</IconBox>
                    <Typography
                      variant='h5'
                      sx={{ fontWeight: 700, mb: 2, color: '#171717' }}
                    >
                      {card.title}
                    </Typography>
                    <Typography sx={{ color: '#6b7280', lineHeight: 1.7, mb: 3 }}>
                      {card.desc}
                    </Typography>
                    {card.bullets.map((b) => (
                      <Box key={b} sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                        <Dot />
                        <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                          {b}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 8, maxWidth: 900, mx: 'auto' }}>
            <Card
              sx={{
                background:
                  'linear-gradient(135deg, rgba(39,177,198,0.1), rgba(39,177,198,0.05)) !important',
                backgroundImage: 'none !important',
                border: '1px solid rgba(39,177,198,0.3)',
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, color: '#0e7490', mb: 2 }}
                >
                  Core Hypothesis
                </Typography>
                <Typography
                  sx={{ color: '#374151', fontSize: '1.0625rem', lineHeight: 1.7 }}
                >
                  Information flows bidirectionally across timeframes: larger
                  timeframes constrain shorter movements while microstructural data
                  provides early signals that propagate upward. Our system captures
                  both dimensions simultaneously for optimal trading performance.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Statistical Trading Edge */}
      <Box sx={{ py: 12, backgroundColor: '#ffffff' }}>
        <Container maxWidth='lg'>
          <SectionHeader
            badge='Trading Edge'
            title='Statistical Trading Edge'
            subtitle='Systematic approaches to market exploitation with quantifiable confidence and rigorous risk controls'
          />
          <Grid container spacing={3}>
            {edgeFeatures.map((f, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, lg: 4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  style={{ height: '100%' }}
                >
                  <StyledCard elevation={0}>
                    <CardContent sx={{ p: 4 }}>
                      <IconBox>{f.icon}</IconBox>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, mb: 2, color: '#171717' }}
                      >
                        {f.title}
                      </Typography>
                      <Typography sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                        {f.desc}
                      </Typography>
                    </CardContent>
                  </StyledCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Data Sources */}
      <Box sx={{ py: 12, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth='lg'>
          <SectionHeader
            badge='Data Sources'
            title='Comprehensive Data Integration'
            subtitle='Our system integrates diverse data streams into a unified framework for superior market intelligence'
          />
          <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
            {dataSources.map((s, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6 }}>
                <StyledCard elevation={0}>
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 4,
                          background:
                            'linear-gradient(135deg, rgba(39,177,198,0.2), rgba(39,177,198,0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {s.icon}
                      </Box>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, color: '#171717' }}
                      >
                        {s.title}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: '#6b7280', lineHeight: 1.7, mb: 2 }}>
                      {s.desc}
                    </Typography>
                    {s.metrics.map((m) => (
                      <Box key={m} sx={{ display: 'flex', gap: 1.5, mb: 0.5 }}>
                        <Dot />
                        <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                          {m}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Performance Metrics */}
      <Box sx={{ py: 12, backgroundColor: '#ffffff' }}>
        <Container maxWidth='lg'>
          <SectionHeader
            badge='Performance'
            title='Performance Metrics'
            subtitle='Research-validated results demonstrating consistent profitability through systematic market exploitation'
          />
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {metrics.map((m, i) => (
              <Grid key={i} size={{ xs: 6, lg: 3 }}>
                <Card
                  sx={{
                    backgroundColor: '#fafafa !important',
                    backgroundImage: 'none !important',
                    border: 'none',
                    borderRadius: 3,
                    textAlign: 'center',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background:
                          'linear-gradient(135deg, rgba(39,177,198,0.2), rgba(39,177,198,0.1))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        color: '#0e7490',
                      }}
                    >
                      {m.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: '#0e7490',
                        mb: 0.5,
                      }}
                    >
                      {m.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#171717',
                        mb: 0.5,
                        fontSize: '0.875rem',
                      }}
                    >
                      {m.label}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{ color: '#9ca3af', display: { xs: 'none', sm: 'block' } }}
                    >
                      {m.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Research Paper */}
      <Box sx={{ py: 12, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth='lg'>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Card
              sx={{
                backgroundColor: '#fafafa !important',
                backgroundImage: 'none !important',
                border: '1px solid rgba(39,177,198,0.2)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  background:
                    'linear-gradient(90deg, rgba(39,177,198,0.1), rgba(39,177,198,0.05))',
                  p: 4,
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      background: 'rgba(39,177,198,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: 32, color: '#0e7490' }} />
                  </Box>
                  <Box>
                    <Typography
                      variant='h5'
                      sx={{ fontWeight: 700, mb: 1, color: '#171717' }}
                    >
                      Research-Backed Technology
                    </Typography>
                    <Typography sx={{ color: '#6b7280', mb: 2 }}>
                      Our approach is documented in a comprehensive research paper
                      published on arXiv, detailing the neural network architecture,
                      multi-timeframe analysis methodology, and performance
                      validation.
                    </Typography>
                    <MuiButton
                      component={Link}
                      href='https://arxiv.org/abs/2508.02356'
                      target='_blank'
                      variant='contained'
                      endIcon={<LaunchIcon />}
                      sx={{
                        backgroundColor: '#27b1c6',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#1e8899', color: 'white' },
                        color: 'white',
                      }}
                    >
                      Read Research Paper
                    </MuiButton>
                  </Box>
                </Box>
              </Box>
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={4}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography sx={{ fontWeight: 600, color: '#171717', mb: 1.5 }}>
                      Key Findings
                    </Typography>
                    {[
                      'Multi-timeframe CNN architecture outperforms traditional LSTM approaches',
                      'Soft attention mechanism critical for reliable confidence scoring',
                      'Integration of orderbook and sentiment data significantly improves accuracy',
                    ].map((f) => (
                      <Box key={f} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Dot />
                        <Typography variant='body2' sx={{ color: '#6b7280' }}>
                          {f}
                        </Typography>
                      </Box>
                    ))}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography sx={{ fontWeight: 600, color: '#171717', mb: 1.5 }}>
                      Publication Details
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                        Title:
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ fontWeight: 500, color: '#171717' }}
                      >
                        Neural Network-Based Algorithmic Trading Systems
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                        Author:
                      </Typography>
                      <Typography variant='body2' sx={{ color: '#171717' }}>
                        Zhāng Wěi
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant='caption' sx={{ color: '#9ca3af' }}>
                        Published:
                      </Typography>
                      <Typography variant='body2' sx={{ color: '#171717' }}>
                        Cornell University
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}
